import { CSSProperties, FC, KeyboardEvent, ReactNode } from 'react';
import TableCell from '@mui/material/TableCell';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardCode,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useUiZoom } from '../../../context/UiZoomContext';

/**
 * Drag-to-reorder table columns (@dnd-kit) for the design's MUI tables — the look stays the design's; a header
 * only lifts while it is being dragged. The order is the caller's to persist.
 */

/** The admin's saved order, with any column it doesn't know yet at the end (in its default place). */
export const orderColumnKeys = (saved: string[] | undefined, defaults: string[]): string[] => {
  const known = new Set(defaults);
  const kept = (saved ?? []).filter((key) => known.has(key));
  return [...kept, ...defaults.filter((key) => !kept.includes(key))];
};

/** The design's --shadow-md, for the header being dragged. */
const DRAG_SHADOW = '0px 4px 12px 0px rgba(16, 24, 40, 0.1)';

interface iSortableHeaderCellProps {
  id: string;
  sx: Record<string, unknown>;
  /** A plain click (e.g. sorting) still works — a drag only starts after a few pixels of movement. */
  onClick?: () => void;
  children?: ReactNode;
}

/** A header that drags sideways to move its column; the others slide aside. Enter sorts, Space picks it up. */
export const SortableHeaderCell: FC<iSortableHeaderCellProps> = ({ id, sx, onClick, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const onKeyDown = (event: KeyboardEvent<HTMLTableCellElement>) => {
    if (event.key === 'Enter' && onClick) {
      event.preventDefault();
      onClick();
      return;
    }
    listeners?.onKeyDown?.(event);
  };
  // dnd-kit measures on screen; inside a zoomed page the translate is in zoomed px.
  const zoom = useUiZoom();
  return (
    <TableCell
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onKeyDown={onKeyDown}
      onClick={onClick}
      sx={{
        ...sx,
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        touchAction: 'none',
        transform: CSS.Translate.toString(transform ? { ...transform, x: transform.x / zoom, y: 0 } : null),
        transition,
        // z-index needs a position; a pinned (sticky) header keeps its own.
        ...(isDragging && { position: (sx.position as CSSProperties['position']) ?? 'relative', zIndex: 3, bgcolor: '#fff', boxShadow: DRAG_SHADOW }),
      }}
    >
      {children}
    </TableCell>
  );
};

interface iDraggableColumnsProps {
  /** Column keys in their current order. */
  keys: string[];
  onReorder: (keys: string[]) => void;
  /** The header row's cells — `SortableHeaderCell`s for the keys, plus any fixed cells. */
  children: ReactNode;
}

/** Only Space picks a header up and drops it, so Enter is left to sort. */
const KEYBOARD_CODES = {
  start: [KeyboardCode.Space],
  cancel: [KeyboardCode.Esc],
  end: [KeyboardCode.Space],
};

/** The browser still clicks the header a drag was dropped on; that click must not also sort. */
const swallowNextClick = () => {
  const swallow = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
  };
  window.addEventListener('click', swallow, { capture: true, once: true });
  // The drop's click comes in the same task; anything later is a real click.
  setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 0);
};

/** Wraps a table so its `SortableHeaderCell`s can be dragged; put it around the whole `Table`. */
export const DraggableColumns: FC<iDraggableColumnsProps> = ({ keys, onReorder, children }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates, keyboardCodes: KEYBOARD_CODES })
  );
  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    swallowNextClick();
    if (!over || active.id === over.id) return;
    onReorder(arrayMove(keys, keys.indexOf(String(active.id)), keys.indexOf(String(over.id))));
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} onDragCancel={swallowNextClick}>
      <SortableContext items={keys} strategy={horizontalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
};
